import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import InsightsIcon from '@mui/icons-material/Insights';
import {
    TimeseriesResponse, executionStatsService, isAbort, viewerTimeZone
} from '../../services/executionStatsService';
import { PanelError, PanelSkeleton, SectionCard } from '../Benchmark/BenchmarkPrimitives';
import StatusTrendChart from './StatusTrendChart';
import WorkflowRunPanel from './WorkflowRunPanel';
import { useWorkflowRunStats } from './useExecutionSummary';
import { FLOW_ATTRIBUTION } from './executionStatsVocabulary';
import { rangeBounds, rangeFor } from './executionStatsWindows';

/**
 * One workflow's run health: its counts, the runs behind them, and a month of
 * runs per day.
 *
 * The trend counts the same runs as the counts above it — the campaign's own,
 * or the flow's when the campaign's runs aren't tagged — so the two never
 * disagree.
 */
const RunHealthPanel: React.FC<{ campaignId?: string | null; flowCode?: string | null }> = ({ campaignId, flowCode }) => {
    const stats = useWorkflowRunStats({ campaignId, flowCode });
    const [trend, setTrend] = useState<TimeseriesResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const tz = useMemo(() => viewerTimeZone(), []);

    const selection = useMemo(() => {
        if (stats.attribution === 'campaign' && campaignId) {
            return { keyType: 'campaign' as const, keys: [campaignId], groupVersions: false };
        }
        if (stats.attribution === 'flow' && flowCode) {
            return { keyType: 'code' as const, keys: [flowCode], groupVersions: true };
        }
        return null;
    }, [stats.attribution, campaignId, flowCode]);

    useEffect(() => {
        if (!selection) return;
        const controller = new AbortController();
        setTrend(null);
        setError(null);
        executionStatsService
            .getTimeseries(
                { scope: 'flow', ...selection, ...rangeBounds(rangeFor('30d')), granularity: 'day', tz },
                { signal: controller.signal }
            )
            .then(setTrend)
            .catch((err) => { if (!isAbort(err)) setError(err?.message || 'Could not load the run trend.'); });
        return () => controller.abort();
    }, [selection, tz, attempt]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '72rem', pb: 2 }}>
            <SectionCard
                title="Run health"
                subtitle="How this workflow's runs ended. Choose a count to see its runs."
                icon={<MonitorHeartIcon fontSize="small" />}
            >
                <WorkflowRunPanel campaignId={campaignId} flowCode={flowCode} />
            </SectionCard>

            <SectionCard
                title="Runs per day"
                subtitle={stats.attribution === 'flow' ? `Last 30 days · ${FLOW_ATTRIBUTION}` : 'Last 30 days, by outcome'}
                icon={<InsightsIcon fontSize="small" />}
            >
                {error ? (
                    <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />
                ) : trend ? (
                    <StatusTrendChart data={trend} />
                ) : stats.error ? (
                    <PanelError message={stats.error} />
                ) : (
                    <PanelSkeleton height={260} />
                )}
            </SectionCard>
        </Box>
    );
};

export default RunHealthPanel;
